import { todayDrafting, todayMatches, todayReviewMatches, todayStepOf, TODAY_PENDING_STEP, TODAY_SEARCH_MAX, type TodayDrafting, type TodayOutlook } from "@swtracker/core/portal/nextStep";
import { useTodayQueue } from "../api/useTodayQueue.ts";
/**
 * Today (AIC-102; rebuilt to the approved 2026-09-30 prototype, Action desk): the signed-in analyst's work in one
 * list. Review items and assignment actions share one row anatomy (initial tile, action title, "Company · issue
 * type", one line, a button with an arrow), prerequisites first (AIC-372 priority and blocks_results). The tabs count
 * Needs you / Assignments / Reviews / Waiting from the server's totals, narrowed to a focused company. The masthead
 * carries the counts, the date and cycle, and a scope row with the analyst, the Outlook control and the sync time; the
 * side panel groups open reviews by company. Admins can look at any confirmed analyst's queue; managers at the queue of
 * anyone in their Entra reporting chain (AIC-124). Paging and search are the server's (AIC-334).
 *
 * Create draft (AIC-108) is optimistic: the row changes at once, one toast goes busy → ok / err, and a failure rolls
 * the row back. The system never sends email for the analyst: drafts land in the analyst's Outlook and the analyst sends them.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError } from "../api/client.ts";
import { describeError, useLoad, usePortal, useRouteReady, useSettledText } from "../api/hooks.ts";
import type { Queue, QueueCycle } from "../api/schemas.ts";
import { ButtonArrow, LineIcon } from "../components/LineIcon.tsx";
import { CalendarDate, CycleTag, Masthead, MastheadCounts, MastheadScope } from "../components/Masthead.tsx";
import { OutlookOutcome, outlookReady, outlookState, useGraphStatus } from "../components/OutlookConnection.tsx";
import { Icon } from "../components/Sprites.tsx";
import { StyledSelect } from "../components/StyledSelect.tsx";
import { RequestMapping } from "../components/RequestMapping.tsx";
import { Skeleton } from "../components/states.tsx";
import { STATES, stateOf, type StateKey } from "../components/status.tsx";
import { outcomeLine, type OutcomeLike } from "../components/interviewOutcome.tsx";
import { useToast } from "../components/Toast.tsx";
import { isAdmin, isTeamManager } from "../roles.ts";
import { Link, useRoute } from "../router.tsx";
import { routeHref, withReturn } from "./navigationContext.ts";
import { TodayOutlookAlert, TodayOutlookControl, openTodayOutlook } from "./TodayOutlook.tsx";
import { TodayReviews } from "./TodayReviews.tsx";
import {
  ASSIGNMENT_REASON, TABS, blockingFor, byPriority, cycleLabel, initialOf, isAction, orderAssignments, plural, reviewCopy, tabStatuses, todayCounts, unsentFlag,
  type Tab, type TodayCounts,
} from "./todayModel.ts";
import "./today.css";
import "./today-desk.css";

type Assignment = Queue["assignments"][number];
interface Override { status?: string; web_link?: string | null; pending?: boolean; just?: boolean }
interface Row extends Assignment { state: StateKey | null; web_link?: string | null; pending?: boolean; just?: boolean }
type Drafting = TodayDrafting;

const CYCLE_STATE: Readonly<Record<string, string>> = { draft: "Draft", ready: "Ready", open: "Open", extended: "Extended", closed: "Closed" };

/** The cycle's key dates (AIC-181): send by, return by and any extension. The cycle tag's tooltip. */
export function keyDates(c: QueueCycle): string {
  return [
    c.invites_by ? `Send by ${shortDay(c.invites_by)}` : "",
    c.return_by ? `Return by ${shortDay(c.return_by)}` : "",
    c.extension_until ? `Extension ${shortDay(c.extension_until)}` : "",
  ].filter(Boolean).join(" · ");
}

/**
 * How fresh the last successful Snowflake sync is (AIC-181), in the prototype's words ("Synced today, 5:03 AM"):
 * within 26 hours (a daily run plus slack) is fresh, within 72 hours ageing, older stale. The words say it too.
 */
export function syncFreshness(lastSync: string | null | undefined, now: Date = new Date()): { tone: "fresh" | "ageing" | "stale" | "none"; text: string } {
  const at = lastSync ? new Date(lastSync) : null;
  if (!at || Number.isNaN(at.getTime())) return { tone: "none", text: "The Data Layer is the source of record" };
  const hours = (now.getTime() - at.getTime()) / 3_600_000;
  const day = (d: Date) => d.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const date = day(at) === day(now) ? "today" : day(at) === day(yesterday) ? "yesterday" : at.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const when = `${date}, ${at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
  if (hours <= 26) return { tone: "fresh", text: `Synced ${when}` };
  if (hours <= 72) return { tone: "ageing", text: `Sync ageing: last synced ${when}` };
  return { tone: "stale", text: `Sync stale: last synced ${when}` };
}

function shortDay(isoDate: string): string {
  const d = new Date(`${isoDate.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return isoDate;
  const sameYear = d.getUTCFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(sameYear ? null : { year: "numeric" }), timeZone: "UTC" });
}

/** "2 days late" for an overdue row (AIC-125); the prototype's rows carry no due date otherwise. */
function lateText(a: Assignment): string | null {
  if (!a.overdue) return null;
  const left = a.days_left ?? null;
  return left !== null && left < 0 ? `${-left} ${plural(-left, "day", "days")} late` : "Overdue";
}

/** AIC-252: how the latest link's interview ended when it stopped part-way ("Partial: 12 of 20 answered, …"). */
function outcomeText(o: OutcomeLike | null | undefined): string | null {
  if (!o || (o.outcome !== "partial" && o.outcome !== "abandoned")) return null;
  return `${outcomeLine(o)}${o.resumable ? " · can resume on the same link" : ""}`;
}

const stepOf = (r: Row, drafting: Drafting) => (r.pending ? TODAY_PENDING_STEP : todayStepOf(r, drafting));

/** Confirmed analysts from GET /api/portal/analysts as the scope row's "{Name}'s work" picker (AIC-124). */
function AnalystPicker({ value, onChange, team, me }: { value: string; onChange: (id: string) => void; team: boolean; me: string }) {
  const list = useLoad((api) => api.analysts(), []);
  if (list.state === "loading") return <span>Loading analysts…</span>;
  if (list.state === "error") return <span role="status">Analysts unavailable. <button type="button" className="ipd-link" onClick={list.reload}>Retry analysts</button></span>;
  return (
    <StyledSelect className="today-who" aria-label={team ? "Team member" : "Analyst"} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{me}’s work</option>
      {list.data.map((a) => <option key={a.id} value={a.id}>{a.label}’s work ({a.upn})</option>)}
    </StyledSelect>
  );
}

/** The row's action: the real destination (the assignment page, Outlook) or the inline draft detail. */
function RowAction({ r, drafting, blocked, open, primary, unsent, onDraft, onPrereq }: {
  r: Row; drafting: Drafting; blocked: boolean; open?: boolean; primary?: boolean;
  /** AIC-433: the draft is old and not detected as sent. Open in Outlook stays the action (it may not be sent yet);
   *  without a link, the action is the assignment's invite, where Record it as sent is. */
  unsent?: boolean;
  onDraft: (r: Row) => void; onPrereq: (r: Row) => void;
}) {
  const route = useRoute();
  const to = withReturn(`/assignments/${r.id}`, routeHref(route));
  const cls = `ipd-btn${primary ? " ipd-btn--go" : " today-row-act"}`;
  const act = primary ? undefined : r.id;
  const [title] = stepOf(r, drafting);
  const label = (verb: string) => `${verb}: ${title} — ${r.company}`;
  if (r.state === "results" && blocked) return <button type="button" className={cls} data-act={act} aria-label={label("Review prerequisite")} onClick={() => onPrereq(r)}>Review prerequisite<ButtonArrow /></button>;
  if (r.state === "results") return <Link to={`${to}#assignment-results`} className={cls} data-act={act} aria-label={label("Review results")}>Review results<ButtonArrow /></Link>;
  if (r.state === "specReady") return <Link to={to} className={cls} data-act={act} aria-label={label("Review spec")}>Review spec<ButtonArrow /></Link>;
  if (r.state === "drafted" && unsent && !r.web_link?.startsWith("https://")) return <Link to={`${to}#assignment-invite`} className={cls} data-act={act} aria-label={label("View invite")}>View invite<ButtonArrow /></Link>;
  if (r.state === "drafted") {
    return r.web_link?.startsWith("https://")
      ? <a className={cls} data-act={act} href={r.web_link} target="_blank" rel="noopener noreferrer" aria-label={`Open in Outlook: ${r.company} (opens Outlook)`}>Open in Outlook<Icon name="ext" /></a>
      : <Link to={to} className={cls} data-act={act} aria-label={label("View draft")}>View draft<ButtonArrow /></Link>;
  }
  if (r.state === "specOk" && (drafting !== "no" || r.pending)) {
    return (
      <button type="button" className={cls} data-act={act} aria-expanded={primary ? undefined : !!open} aria-controls={primary ? undefined : `draft-${r.id}`}
        aria-busy={r.pending || undefined} aria-disabled={r.pending || undefined}
        aria-label={r.pending ? `Creating draft: ${r.company}` : label("Create draft")} onClick={() => { if (!r.pending) onDraft(r); }}>
        {r.pending ? "Creating…" : <>Create draft<ButtonArrow /></>}
      </button>
    );
  }
  if (r.state === "follow") return <Link to={to} className={cls} data-act={act} aria-label={label("Follow up")}>Follow up<ButtonArrow /></Link>;
  return <Link to={to} className={cls} data-act={act} aria-label={label("View assignment")}>View assignment<ButtonArrow /></Link>;
}

export const SKELETON_ROWS = 6;
const SK_W = ["62%", "48%", "70%", "55%", "66%", "44%"];
/** The loading list: skeleton rows in the real row anatomy (tile, title, meta, line, button). */
export function QueueSkeleton({ rows = SKELETON_ROWS }: { readonly rows?: number }) {
  return (
    <>
      <p className="ipd-sr" role="status">Loading your work</p>
      {Array.from({ length: rows }, (_, i) => (
        <div className="today-row today-sk" aria-hidden="true" key={i}>
          <span className="today-tile" />
          <div className="today-row-copy"><Skeleton width={SK_W[i % 6]} /><Skeleton variant="sub" width={SK_W[(i + 2) % 6]} /><Skeleton variant="sub" width={SK_W[(i + 1) % 6]} /></div>
          <Skeleton variant="btn" width="118px" />
        </div>
      ))}
    </>
  );
}

/**
 * AIC-992: the suggested step's space while the queue loads, in its anatomy (label, title, reason, action), so the
 * work below doesn't move down when the suggestion arrives. Hidden from assistive technology.
 */
function NextSkeleton() {
  return (
    <div className="today-next today-next-sk" aria-hidden="true">
      <div className="today-next-copy">
        <span className="today-next-label"><LineIcon name="check" />Suggested next step</span>
        <span className="today-next-sk-title"><Skeleton width="52%" /><Skeleton width="34%" /></span>
        <span className="today-next-sk-line"><Skeleton variant="sub" width="78%" /><Skeleton variant="sub" width="46%" /></span>
      </div>
      <Skeleton variant="btn" width="132px" />
    </div>
  );
}

const ORDER = ["assigned", "spec_ready", "spec_approved", "invite_drafted", "sent", "interviewed", "results_ready", "approved", "exported"];
const STAGES: readonly { label: string; from: string; done?: boolean }[] = [
  { label: "Synced", from: "assigned" }, { label: "Spec approved", from: "spec_approved" }, { label: "Invite drafted", from: "invite_drafted" },
  { label: "Sent", from: "sent" }, { label: "Interviewed", from: "interviewed" },
  { label: "Results approved", from: "approved", done: true }, { label: "Exported", from: "exported", done: true },
];

/** Cumulative stage counts: each stage counts everything that has got at least that far (withdrawn excluded). */
export function cumulativeStages(counts: readonly { status: string; count: number }[]) {
  const at = (from: string) => counts.filter((c) => ORDER.indexOf(c.status) >= ORDER.indexOf(from) && ORDER.includes(c.status)).reduce((n, c) => n + c.count, 0);
  const total = at("assigned");
  return { total, stages: STAGES.map((s) => ({ ...s, n: at(s.from) })) };
}

/**
 * "Across the cycle": the dashboard's counts in four rows, behind a disclosure (prototype .cycle-disclosure). AIC-992:
 * the counts are read when the disclosure first opens, not with the page (it starts closed), and kept after that.
 */
function CycleSummary() {
  const [opened, setOpened] = useState(false);
  return (
    <details className="today-cycle" onToggle={(e) => { if (e.currentTarget.open) setOpened(true); }}>
      <summary><span><LineIcon name="cycle" />Across the cycle</span></summary>
      {opened ? <CycleCounts /> : null}
    </details>
  );
}

function CycleCounts() {
  const load = useLoad((api) => api.dashboard(), []);
  const d = load.state === "ready" ? load.data : null;
  const { total, stages } = cumulativeStages(d?.status_counts ?? []);
  const n = (label: string) => stages.find((s) => s.label === label)?.n ?? 0;
  const whose = d?.scope === "team" ? "Your team" : d?.scope === "analyst" ? "Your assignments" : "All analysts";
  return (
    <div className="today-cycle-body" aria-busy={load.state === "loading" || undefined}>
      {load.state === "loading" ? <p>Loading cycle counts…</p>
        : load.state === "error" ? <p role="status">Cycle counts didn't load. <button type="button" className="ipd-link" onClick={() => load.reload()}>Try again</button></p>
          : !d || !d.mapped || total === 0 ? <p>Counts appear once assignments sync from the Data Layer.</p>
            : <>
              <p>{whose}{d.cycle ? ` · ${cycleLabel(d.cycle)}` : ""}</p>
              <dl>
                <div><dt>Active assignments</dt><dd>{total}</dd></div>
                <div><dt>Interview completed</dt><dd>{n("Interviewed")}</dd></div>
                <div><dt>Results approved</dt><dd>{n("Results approved")}</dd></div>
                <div><dt>Exported</dt><dd>{n("Exported")}</dd></div>
              </dl>
              <Link className="ipd-btn ipd-btn--text" to="/dashboard">View cycle progress<ButtonArrow /></Link>
            </>}
    </div>
  );
}

function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return <div className="today-empty"><h3>{title}</h3>{children ? <p>{children}</p> : null}{action}</div>;
}

export function Today() {
  const { api, roles, name, email, expire, preview = false, previewScope, buildMode = true } = usePortal();
  const toast = useToast();
  const route = useRoute();
  const [analystId, setAnalystId] = useState("");
  const graph = useGraphStatus();
  const [tab, setTab] = useState<Tab>("you");
  const [search, setSearch] = useState("");
  const [company, setCompany] = useState("");
  const oState = outlookState(graph);
  const outlook: TodayOutlook = outlookReady(oState) ? "connected" : oState === "off" ? "disconnected" : oState === "reconnect" ? "reconnect" : "unknown";
  const draftable = !preview && !analystId;
  const settledSearch = useSettledText(search.trim());
  const searchTooLong = [...search.trim()].length > TODAY_SEARCH_MAX;
  // AIC-992: the queue doesn't wait for /me/graph. Without `outlook` the server words the caller's own queue from their
  // own connection (the same read); the portal's view is sent only once it is known to differ (a change on this page, a
  // failed check), which reads the queue again, as a changed Outlook state always did.
  const [sendOutlook, setSendOutlook] = useState(false);
  const queueLoad = useTodayQueue(analystId, {
    q: settledSearch || undefined, company: company || undefined, outlook: sendOutlook ? outlook : undefined,
    display_status: tabStatuses(tab === "wait" ? "wait" : "you", draftable).join(","),
  }, !searchTooLong);
  const load = search.trim() !== settledSearch || searchTooLong ? { ...queueLoad, state: "loading" as const } : queueLoad;
  useRouteReady(load.state === "ready");
  const [selected, setSelected] = useState<string | null>(null);
  const [draftOpen, setDraftOpen] = useState<string | null>(null);
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const [resolved, setResolved] = useState<ReadonlyMap<string, string>>(new Map());
  const [over, setOver] = useState<Readonly<Record<string, Override>>>({});
  const [focusRow, setFocusRow] = useState<string | null>(null);
  const [announce, setAnnounce] = useState("");
  const [completion, setCompletion] = useState(false);
  const team = isTeamManager(roles);
  const queueTitle = useRef<HTMLHeadingElement>(null);

  useEffect(() => { setOver({}); setTab("you"); setSearch(""); setCompany(""); setSelected(null); setDraftOpen(null); setClosed(new Set()); setResolved(new Map()); }, [analystId]);
  const q = load.state === "ready" ? load.data : null;
  useEffect(() => { if (q) setClosed(new Set()); }, [q]);
  const rows = useMemo<Row[]>(() => {
    if (!q) return [];
    return q.assignments.map((a) => {
      const o = over[a.id] ?? {};
      const status = o.status ?? a.status;
      // AIC-181: the derived display status (Interview scheduled / Needs follow-up) unless this page just changed the
      // row; the draft link is the caller's own mailbox's, from the server or from the draft just created.
      const shown = o.status ?? a.display_status ?? a.status;
      return { ...a, status, display_status: shown, state: stateOf(shown), web_link: o.status ? o.web_link : a.draft_web_link, pending: o.pending, just: o.just };
    });
  }, [q, over]);

  const ownQueue = analystId === "" && q?.scope !== "team";
  const portalDrafting: Drafting = todayDrafting({ preview, ownQueue, outlook });
  // Until /me/graph answers, the rows use the drafting context the server worded them in.
  const drafting: Drafting = graph.state === "loading" && q?.drafting ? q.drafting : portalDrafting;
  const differs = graph.state !== "loading" && !!q?.mapped && !!q.drafting && q.drafting !== portalDrafting;
  useEffect(() => { if (differs) setSendOutlook(true); }, [differs]);
  const canDraftAction = drafting !== "no";
  const reviews = useMemo(() => byPriority((q?.open_review_items ?? []).filter((r) => !closed.has(r.id))), [q, closed]);
  const blockedBy = (r: Row) => (r.state === "results" ? blockingFor(r, reviews) : []);
  const isBlocked = (r: Row) => blockedBy(r).length > 0;
  const actionRow = (r: Row) => isAction(r.state, canDraftAction) || !!r.pending;
  const inCompany = (c: string) => !company || c === company;
  const showReviews = tab === "you" || tab === "reviews";
  const shownReviews = showReviews ? reviews.filter((r) => inCompany(r.company) && todayReviewMatches(r, search)) : [];
  const assignmentMatches = (r: Row) => inCompany(r.company) && todayMatches(`${r.company} ${stepOf(r, drafting).join(" ")}`, search);
  const shownAssignments = tab === "reviews" ? [] : tab === "wait"
    ? rows.filter((r) => !actionRow(r) && assignmentMatches(r))
    : orderAssignments(rows.filter((r) => actionRow(r) && assignmentMatches(r)), isBlocked);
  const shownCount = shownReviews.length + shownAssignments.length;

  // Counts: the scope's (masthead, header) and the focused company's (tabs). Cached so a tab switch keeps them.
  const countCache = useRef<{ analyst: string; all: TodayCounts | null; scoped: TodayCounts | null } | null>(null);
  const allCounts = todayCounts(q, { company: "", draftable: canDraftAction, rows, reviews });
  const scopedCounts = todayCounts(q, { company, draftable: canDraftAction, rows, reviews });
  if (q) countCache.current = { analyst: analystId, all: allCounts, scoped: company ? scopedCounts : allCounts };
  const cached = countCache.current?.analyst === analystId ? countCache.current : null;
  const failed = load.state === "error";
  const counts = failed ? null : q ? scopedCounts : company ? null : cached?.scoped ?? null;
  const totals = failed ? null : q ? allCounts : cached?.all ?? null;

  const companyTotals = q?.totals?.by_company?.filter((c) => c.open_review_items > 0);
  const loadedCompanies = q ? companyTotals?.map((c) => ({ company: c.company, open: c.open_review_items }))
    ?? [...new Set(reviews.map((r) => r.company))].map((c) => ({ company: c, open: reviews.filter((r) => r.company === c).length })) : null;
  // The company index is the scope's (by_company ignores every filter): keep it on screen while a filtered page loads.
  const companyCache = useRef<{ analyst: string; list: { company: string; open: number }[] } | null>(null);
  if (loadedCompanies) companyCache.current = { analyst: analystId, list: loadedCompanies };
  const companies = loadedCompanies ?? (companyCache.current?.analyst === analystId ? companyCache.current.list : null);
  // The cycle tag: /me/queue's `cycles` (every cycle of the scope, not just the rows loaded), when there is exactly one.
  // tracker-api always sends them (AIC-391); the loaded rows are never read for this, so one page can't decide it.
  // AIC-992: like the company index, the scope's cycles stay on screen while a filtered page loads. Kept per view: the
  // analyst picked, and whose preview (if any), so a preview never shows the admin's own cycle tag ("" is both's own).
  const cycleCache = useRef<{ view: string; cycles: readonly QueueCycle[] } | null>(null);
  const cycleView = JSON.stringify([previewScope ?? null, analystId]);
  if (q?.cycles) cycleCache.current = { view: cycleView, cycles: q.cycles };
  const cycles = q ? q.cycles ?? [] : cycleCache.current?.view === cycleView ? cycleCache.current.cycles : null;
  const cycle = cycles?.length === 1 ? cycles[0]!.id : null;
  const cycleInfo = cycles?.find((c) => c.id === cycle) ?? null;
  const who = q?.analyst ? `${q.analyst.owner_name}${q.scope === "team" ? " (your team)" : ""}` : (name || email);
  const status = load.state === "loading" ? "loading" : failed ? "error" : !q?.mapped ? "unmapped" : "ready";
  const readOnly = !!analystId || preview;

  // Announce a tab or company change (never the first render).
  const lastView = useRef(`${tab}|${company}`);
  useEffect(() => {
    if (load.state !== "ready") return;
    if (lastView.current === `${tab}|${company}`) return;
    lastView.current = `${tab}|${company}`;
    setAnnounce(`Showing ${shownCount} ${plural(shownCount, "item", "items")}${company ? ` for ${company}` : ""}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, company, q, load.state]);

  useEffect(() => {
    if (!focusRow) return;
    for (const el of document.querySelectorAll<HTMLElement>("[data-act]")) if (el.dataset.act === focusRow) el.focus();
    setFocusRow(null);
  }, [focusRow, over]);

  useEffect(() => { if (draftOpen) document.getElementById(`draft-${draftOpen}`)?.focus({ preventScroll: true }); }, [draftOpen]);
  const focusQueue = () => requestAnimationFrame(() => { queueTitle.current?.focus({ preventScroll: true }); queueTitle.current?.scrollIntoView?.({ block: "nearest" }); });
  const openReview = (id: string | null) => { setDraftOpen(null); setSelected(id); };
  const toggleDraft = (r: Row) => { setSelected(null); setDraftOpen((d) => (d === r.id ? null : r.id)); };
  const closeDraft = () => { const id = draftOpen; setDraftOpen(null); if (id) setFocusRow(id); };
  const chooseTab = (t: Tab) => { setTab(t); setSelected(null); setDraftOpen(null); };
  const chooseCompany = (c: string) => { setCompany(c); setTab("reviews"); setSearch(""); setSelected(null); setDraftOpen(null); focusQueue(); };
  const clearCompany = () => { setCompany(""); focusQueue(); };
  // A blocked results row routes into its prerequisite: Reviews, that company, the blocking item open.
  const openPrereq = (r: Row) => { const first = blockedBy(r)[0]; setCompany(r.company); setTab("reviews"); setSearch(""); setDraftOpen(null); if (first) setSelected(first.id); };
  const settle = (id: string, value: boolean, c: string) => {
    setClosed((old) => { const next = new Set(old); if (value) next.add(id); else next.delete(id); return next; });
    setResolved((old) => { const next = new Map(old); if (value) next.set(id, c); else next.delete(id); return next; });
    load.reload();
  };
  const resolvedFor = (c: string) => [...resolved.values()].filter((x) => x === c).length;
  const resultsFor = (item: { company?: string | null; assignment_id?: string | null }) => {
    const r = rows.find((x) => x.state === "results" && (item.assignment_id ? x.id === item.assignment_id : x.company === item.company));
    return r ? { href: `${withReturn(`/assignments/${r.id}`, routeHref(route))}#assignment-results` } : undefined;
  };
  const onCompletion = useCallback((shown: boolean) => setCompletion(shown), []);

  // Escape closes the open detail (the Outlook popover takes Escape first, and dialogs and menus keep theirs).
  const escape = useRef<() => void>(() => {});
  escape.current = () => { if (selected) openReview(null); else if (draftOpen) closeDraft(); };
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === "Escape" && !e.defaultPrevented && !(e.target as Element | null)?.closest?.("dialog, [role=listbox]")) escape.current(); };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, []);

  const create = async (r: Row) => {
    if (drafting !== "yes" || preview || r.pending) return;
    setDraftOpen(null);
    setOver((o) => ({ ...o, [r.id]: { pending: true } }));
    const t = toast.show({ tone: "busy", title: `Creating ${r.company}'s invite draft…`, desc: "It will appear in your Outlook Drafts." });
    try {
      const out = await api.createInviteDraft(r.id, false);
      setOver((o) => ({ ...o, [r.id]: { status: "invite_drafted", web_link: out.draft.web_link, just: true } }));
      const notes = [
        out.draft.mode === "build" ? "Build mode: addressed to the build tester." : "",
        out.attachment === "not_imported" ? "The thank-you PDF isn't attached: the invite templates aren't imported yet." : "",
      ].filter(Boolean).join(" ");
      t.update({
        tone: "ok",
        title: out.status === "exists" ? `${r.company}'s invite draft is already in your Outlook` : `Invite draft created for ${r.company}`,
        desc: `It's in your Outlook Drafts — you send it.${notes ? ` ${notes}` : ""}`,
      });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) expire();
      setOver((o) => { const next = { ...o }; delete next[r.id]; return next; }); // visible rollback
      const err = e instanceof Error ? e : new Error(String(e));
      const outlookIssue = e instanceof ApiError && e.status === 409 && /not_connected|graph|reconnect/.test(`${e.code ?? ""} ${e.detail ?? ""}`);
      if (outlookIssue) graph.reload();
      t.update({
        tone: "err",
        title: "The draft wasn't created",
        desc: `${describeError(err)} ${r.company}'s row is unchanged.`,
        action: outlookIssue ? { label: "Go to Outlook", onAction: openTodayOutlook } : { label: "Try again", onAction: () => void create(r) },
      });
    }
    setFocusRow(r.id);
  };

  // The suggested next step: the first item in view that can be done now (never a blocked result or an offline draft).
  const nextReview = load.state === "ready" ? shownReviews[0] : undefined;
  const nextAssignment = load.state === "ready" && !nextReview && tab !== "wait"
    ? shownAssignments.find((r) => actionRow(r) && !r.pending && !isBlocked(r) && !(r.state === "specOk" && drafting !== "yes")) : undefined;
  let suggestion = null;
  if (nextReview) {
    const c = reviewCopy(nextReview);
    suggestion = { title: `${c.title} · ${nextReview.company}`, reason: nextReview.suggestion ?? c.line,
      action: <button type="button" className="ipd-btn ipd-btn--go" aria-label={`${c.verb}: ${c.title} — ${nextReview.company}`} onClick={() => openReview(nextReview.id)}>{c.verb}<ButtonArrow /></button> };
  } else if (nextAssignment) {
    const [title, line] = stepOf(nextAssignment, drafting);
    const unsent = unsentFlag(nextAssignment, readOnly);
    suggestion = { title: `${title} · ${nextAssignment.company}`, reason: unsent?.line ?? (nextAssignment.state && ASSIGNMENT_REASON[nextAssignment.state]) ?? line,
      action: <RowAction r={nextAssignment} drafting={drafting} blocked={false} primary unsent={!!unsent} onDraft={toggleDraft} onPrereq={openPrereq} /> };
  }

  // AIC-992: while a page loads, keep the suggestion's space if the last view had one (the first load assumes it will).
  const hadSuggestion = useRef(true);
  if (load.state === "ready") hadSuggestion.current = suggestion !== null;
  const nextSkeleton = !suggestion && load.state === "loading" && !searchTooLong && hadSuggestion.current;

  const hasActions = (totals?.actions ?? 0) + (totals?.reviews ?? 0) > 0;
  let body: ReactNode = null;
  if (searchTooLong) body = <Empty title="Shorten your search to continue.">Use {TODAY_SEARCH_MAX} characters or fewer.</Empty>;
  else if (load.state === "loading") body = <QueueSkeleton />;
  else if (load.state === "error") {
    body = <>
      <Empty title="Your queue couldn’t load" action={<button type="button" className="ipd-btn" onClick={() => load.reload()}>Try again</button>}>Your assignments and reviews haven’t changed.<br />Refresh to try loading them again.</Empty>
      <p className="ipd-sr" role="alert">{describeError(load.error)}</p>
    </>;
  } else if (!q?.mapped) {
    body = team && analystId === ""
      ? <Empty title="You have no analyst queue of your own.">Choose a team member above to see theirs.</Empty>
      : <Empty title="Your account isn't mapped to an analyst yet." action={<RequestMapping />}>{q?.message ?? "An admin maps your IDC account to a Software Tracker analyst; your queue appears here then."}</Empty>;
  } else if (shownCount === 0 && !completion) {
    const waiting = counts?.waiting ?? 0;
    const seeWaiting = <button type="button" className="ipd-btn" onClick={() => { setCompany(""); setSearch(""); chooseTab("wait"); }}>See what’s waiting</button>;
    body = search.trim()
      ? <Empty title="No matching work" action={<button type="button" className="ipd-btn" onClick={() => setSearch("")}>Clear search</button>}>Try another company or next step.</Empty>
      : tab === "wait" ? <Empty title="Nothing is waiting">Nothing is waiting on a vendor or the system{company ? ` for ${company}` : ""}.</Empty>
        : company ? <Empty title="No work in this view" action={<button type="button" className="ipd-btn" onClick={clearCompany}>Show all companies</button>}>Choose another work type or clear the company filter.</Empty>
          : hasActions ? <Empty title="No work in this view" action={waiting ? seeWaiting : undefined}>{waiting ? `${waiting} ${plural(waiting, "assignment is", "assignments are")} waiting on others.` : "Choose another work type."}</Empty>
            : <Empty title="You’re caught up" action={waiting ? seeWaiting : undefined}>{waiting ? `Your remaining ${plural(waiting, "assignment is", "assignments are")} waiting on others.` : "New assignments and review items will appear here when they need you."}</Empty>;
  } else {
    body = shownAssignments.map((r) => {
      const [title, line] = stepOf(r, drafting);
      const blocking = blockedBy(r);
      const late = lateText(r);
      const unsent = unsentFlag(r, readOnly);
      const outcome = outcomeText(r.interview_outcome);
      const open = draftOpen === r.id;
      return [
        <article key={r.id} className={`today-row${r.just ? " is-just" : ""}${open ? " is-open" : ""}`}>
          <span className="today-tile" aria-hidden="true">{initialOf(r.company)}</span>
          <div className="today-row-copy">
            <h3>{title}</h3>
            <p className="today-row-meta"><b>{r.company}</b><span aria-hidden="true">·</span>
              <span>{blocking.length ? `${blocking.length} review ${plural(blocking.length, "item", "items")} to resolve` : r.state ? STATES[r.state].label : r.status.replace(/_/g, " ")}</span>
              {late ? <><span aria-hidden="true">·</span><span className="today-late">{late}</span></> : null}
              {unsent ? <><span aria-hidden="true">·</span><span className="today-late">{unsent.flag}</span></> : null}</p>
            <p className="today-row-line">{blocking.length ? `Resolve the open review ${plural(blocking.length, "item", "items")} before approving these results.` : unsent ? (
              <>{unsent.line} <Link to={`${withReturn(`/assignments/${r.id}`, routeHref(route))}#assignment-invite`} className="ipd-link" aria-label={`${readOnly ? "View invite" : "Go to Record it as sent"}: ${r.company}`}>{readOnly ? "View invite" : "Go to Record it as sent"}</Link></>
            ) : outcome ?? line}</p>
          </div>
          <RowAction r={r} drafting={drafting} blocked={blocking.length > 0} open={open} unsent={!!unsent} onDraft={toggleDraft} onPrereq={openPrereq} />
        </article>,
        open ? (
          <section key={`${r.id}-draft`} id={`draft-${r.id}`} className="today-detail" tabIndex={-1} aria-labelledby={`draft-h-${r.id}`}>
            <div className="today-detail-top"><span>{r.company} · {STATES.specOk.label}</span><button type="button" className="ipd-btn" onClick={closeDraft}>Close details</button></div>
            <h2 id={`draft-h-${r.id}`}>{title}</h2>
            <p className="today-detail-body">{line}</p>
            <div className="today-source"><b>What happens next</b><p>{buildMode ? "In build mode, the invitation draft goes to the build tester’s Outlook. No email is sent automatically." : "The invitation draft goes to your Outlook Drafts. You send it; nothing is sent for you."}</p></div>
            <div className="today-detail-actions"><button type="button" className="ipd-btn ipd-btn--go" disabled={drafting !== "yes"} aria-label={`Create the invite draft for ${r.company}`} onClick={() => void create(r)}>Create draft</button></div>
            <small className="today-detail-foot">{drafting === "yes" ? "Drafts only: Software Tracker never sends email for you." : drafting === "reconnect" ? "Reconnect Outlook using the notice above to enable draft creation." : drafting === "unsure" ? "Create draft turns on once Outlook is confirmed connected. Check the Outlook control above." : "Connect Outlook using the notice above to enable draft creation."}</small>
          </section>
        ) : null,
      ];
    });
  }

  const reviewsVisible = load.state === "ready" && !!q?.mapped && !searchTooLong && showReviews;
  const matchingTotal = q?.totals ? (tab === "reviews" ? q.totals.matching_review_items : tab === "you" ? q.totals.matching + q.totals.matching_review_items : q.totals.matching) : shownCount;
  const summary = failed ? "Counts will return when the queue loads." : load.state !== "ready" ? "Loading your work…"
    : `${matchingTotal > shownCount ? `${shownCount} of ${matchingTotal}` : shownCount} ${plural(matchingTotal, "item", "items")} shown${company ? ` for ${company}` : " · All your companies"} · ${tab === "wait" ? "No action needed on these items" : "Waiting items are in their own view"}`;
  const sync = syncFreshness(q?.last_sync_at ?? null);
  const intro = status === "loading" ? <span className="today-intro-wait">Loading your queue…</span> : status === "error" ? "Your work couldn’t load. Try refreshing your queue." : status === "unmapped" ? "Your queue isn't set up yet."
    : totals && totals.actions + totals.reviews > 0
      ? <MastheadCounts parts={[[totals.actions, plural(totals.actions, "assignment action", "assignment actions")], [totals.reviews, plural(totals.reviews, "review item", "review items")]]} tail="ready for your attention" />
      : "Nothing needs you right now.";
  const now = new Date();
  const waitingAll = totals?.waiting ?? 0;

  return (
    <div className="ipd-today today-desk">
      <Masthead title="Today" titleId="today-h" intro={intro}
        aside={<><CalendarDate date={now} />{cycle ? <span title={cycleInfo ? keyDates(cycleInfo) || undefined : undefined}><CycleTag label={cycleLabel(cycle)} state={cycleInfo ? `${CYCLE_STATE[cycleInfo.state] ?? cycleInfo.state} cycle` : undefined} /></span>
          // AIC-992: the cycle tag's space until the first answer says whether there is one (its words are CSS only).
          : !cycles && !failed ? <span className="ipd-cycle-tag today-cycle-slot" aria-hidden="true"><b /></span> : null}</>}
        scope={
          <MastheadScope who={isAdmin(roles) || team ? <AnalystPicker value={analystId} onChange={setAnalystId} team={team} me={name || email} /> : `${who}’s work`}>
            <TodayOutlookControl load={graph} />
            {readOnly ? <span className="today-readonly">Read-only analyst view</span> : null}
            <span className="ipd-masthead-push today-sync" data-sync={sync.tone}>{failed ? "Sync time unavailable" : load.state === "loading" && !q ? "Checking the last sync…" : sync.text}</span>
            <button type="button" className="ipd-btn ipd-btn--text today-refresh" onClick={() => load.reload()}><LineIcon name="refresh" />Refresh</button>
          </MastheadScope>
        } />
      <OutlookOutcome outcome={route.query.get("outlook")} />
      {ownQueue && !preview ? <TodayOutlookAlert state={oState} /> : null}
      <div className="today-workspace">
        <div className="today-main">
          {suggestion ? (
            <section className="today-next" aria-label="Suggested next step">
              <div className="today-next-copy"><span className="today-next-label"><LineIcon name="check" />Suggested next step</span><h2 id="next-h">{suggestion.title}</h2><p>{suggestion.reason}</p></div>
              {suggestion.action}
            </section>
          ) : nextSkeleton ? <NextSkeleton /> : null}
          <section className="today-queue" id="queue" aria-labelledby="queue-h" aria-busy={load.state === "loading" || undefined}>
            <div className="today-queue-head"><h2 id="queue-h" ref={queueTitle} tabIndex={-1}>{company ? `${company} · Your work` : "Your work"}</h2>
              <span>{failed ? "Counts unavailable" : company ? "Company view" : totals ? `${totals.actions} assignment ${plural(totals.actions, "action", "actions")} · ${totals.reviews} review ${plural(totals.reviews, "item", "items")}` : ""}</span></div>
            <div className="today-tabs" role="group" aria-label="Filter your work">
              {TABS.map((t) => {
                const n = counts ? (t.id === "you" ? counts.actions + counts.reviews : t.id === "assignments" ? counts.actions : t.id === "reviews" ? counts.reviews : counts.waiting) : null;
                return <button type="button" key={t.id} aria-pressed={tab === t.id} disabled={load.state !== "ready"} onClick={() => chooseTab(t.id)}>{t.label}{" "}<span>{n ?? "—"}</span></button>;
              })}
            </div>
            {company ? <div className="today-company-scope"><span>Company: {company}</span><button type="button" className="ipd-btn" aria-label="Remove company filter" onClick={clearCompany}>Clear company</button></div> : null}
            <div className="today-search">
              <label><LineIcon name="search" /><input type="search" aria-label="Find a company or next step" placeholder="Find a company or next step" value={search} onChange={(e) => setSearch(e.target.value)} /></label>
              {search ? <button type="button" className="ipd-btn" onClick={() => setSearch("")}>Clear search</button> : null}
              <span className="today-sort">{tab === "wait" ? "Waiting on others" : "Prerequisites first"}</span>
            </div>
            {searchTooLong ? <p className="ipd-sr" role="alert">Use {TODAY_SEARCH_MAX} characters or fewer to search.</p> : null}
            <p className="ipd-sr" aria-live="polite">{announce}</p>
            <div className="today-list" id="queue-body">
              <TodayReviews key={analystId} visible={reviewsVisible} viewKey={`${tab}|${company}|${search}`} items={shownReviews} selected={selected} onSelect={openReview}
                readOnly={readOnly} onSettled={settle} resolvedFor={resolvedFor} resultsFor={resultsFor} onCompletion={onCompletion}
                onContinue={(next) => { setCompany(next.company); setTab("reviews"); setSearch(""); setSelected(next.id); }}
                onBack={(all) => { if (all) { setCompany(""); setSearch(""); setTab("you"); } focusQueue(); }} />
              {body}
            </div>
            {load.state === "ready" && (q?.assignment_cursor && tab !== "reviews" || q?.review_cursor && showReviews || load.moreError) ? (
              <div className="today-more">
                {tab !== "reviews" && q?.assignment_cursor ? <button type="button" className="ipd-btn" disabled={!!load.busy} onClick={() => void load.more("assignments")}>{load.busy === "assignments" ? "Loading assignments…" : "Load more assignments"}</button> : null}
                {showReviews && q?.review_cursor ? <button type="button" className="ipd-btn" disabled={!!load.busy} onClick={() => void load.more("reviews")}>{load.busy === "reviews" ? "Loading reviews…" : "Load more reviews"}</button> : null}
                {load.moreError ? <p role="alert">{describeError(load.moreError)} Use Load more to try again.</p> : null}
              </div>
            ) : null}
            <p className="today-summary" role="status">{summary}</p>
          </section>
        </div>
        <aside className="today-context" aria-label="Review by company">
          <section className="today-day">
            <div className="today-day-head"><h2>Review by company</h2>{cycle ? <span>{cycleLabel(cycle)}</span> : null}</div>
            <p className="today-day-intro">Work through a company’s review items together.</p>
            <div className="today-company-head"><h3>Companies with open reviews</h3><span>{failed || !companies ? "—" : companies.length}</span></div>
            {failed ? <p className="today-day-empty">Your review summary will appear when the queue loads.</p>
              : !companies ? <p className="today-day-empty">Loading companies…</p>
                : companies.length ? <div className="today-companies">{companies.map((c) => (
                  <button type="button" key={c.company} className="today-company" aria-pressed={company === c.company} onClick={() => chooseCompany(c.company)}>
                    <span className="today-company-tile" aria-hidden="true">{initialOf(c.company)}</span>
                    <span><strong>{c.company}</strong><small>{c.open} open review {plural(c.open, "item", "items")}</small></span>
                    <LineIcon name="arrow" />
                  </button>
                ))}</div> : <p className="today-day-empty">No open reviews. You’re up to date.</p>}
            <p className="today-day-note">{failed ? "Queue data is currently unavailable." : resolved.size ? `${resolved.size} review ${plural(resolved.size, "item", "items")} resolved on this page visit.` : "Select a company to focus your review queue."}</p>
            <div className="today-waiting"><span className="today-dot" aria-hidden="true" />
              <span>{failed ? "Waiting status unavailable." : !totals ? "Checking what’s waiting…" : waitingAll ? `${waitingAll} ${plural(waitingAll, "assignment is", "assignments are")} waiting on others.` : "Nothing waiting on others."}</span>
              {!failed && waitingAll ? <button type="button" className="ipd-btn" aria-label="See waiting assignments" onClick={() => { setCompany(""); setSearch(""); chooseTab("wait"); }}><LineIcon name="arrow" /></button> : null}
            </div>
          </section>
          <CycleSummary />
        </aside>
      </div>
      <footer className="today-foot">The Data Layer is the source of record. Drafts are created in your Outlook, and you send them.</footer>
    </div>
  );
}

